import { observable } from 'mobx';
import { observer } from 'mobx-react';
import { Component } from 'react';
import { Card } from 'react-bootstrap';
import { sleep } from 'web-utility';

import { Step1SelectFiles } from '../component/DocumentUpload/Step1SelectFiles';
import { Step2Preview } from '../component/DocumentUpload/Step2Preview';
import { Step3Processing } from '../component/DocumentUpload/Step3Processing';
import { Chunk, MOCK_TOTAL_CHUNKS, mockChunks } from '../model/DocumentChunk';
import { t } from '../model/Translation';

@observer
export class DocumentUploadPage extends Component {
    get steps() {
        return [
            { number: 1, label: t('select_data_source') },
            { number: 2, label: t('text_chunking_cleaning') },
            { number: 3, label: t('process_complete') }
        ];
    }

    @observable
    accessor currentStep = 1;

    @observable
    accessor selectedFiles: File[] = [];

    @observable
    accessor uploadStatus: 'idle' | 'uploading' | 'success' | 'error' = 'idle';

    @observable
    accessor errorMessage: string | undefined;

    @observable
    accessor chunks: Chunk[] = mockChunks;

    @observable
    accessor selectedFileIndex = 0;

    @observable
    accessor totalChunks = 0;

    @observable
    accessor uploadProgress = 0;

    @observable
    accessor processingFiles: Map<string, number> = new Map();

    handleFilesChange = (files: File[]) => {
        this.selectedFiles = files;
    };

    handleFileIndexChange = (index: number) => {
        this.selectedFileIndex = index;
    };

    handleNextStep = async () => {
        if (this.currentStep === 1 && this.selectedFiles.length > 0) {
            // Generate mock chunks when moving to step 2
            await this.generateChunks();
        }
        if (this.currentStep === 2) {
            // Start processing when moving to step 3
            this.startProcessing();
        }
        this.currentStep += 1;
    };

    handlePrevStep = () => (this.currentStep -= 1);

    generateChunks = async () => {
        // Mock chunk generation - in real implementation, this would call backend API
        this.chunks = mockChunks;
        this.totalChunks = MOCK_TOTAL_CHUNKS;
    };

    startProcessing = () => {
        this.uploadStatus = 'uploading';

        // Simulate file processing progress
        this.selectedFiles.forEach(file => {
            this.processingFiles.set(file.name, 0);
            this.simulateProgress(file.name);
        });
    };

    simulateProgress = async (fileName: string) => {
        // Simulate progress from 0 to 100
        for (let i = 0; i <= 100; i += 10) {
            await sleep(0.5);
            this.processingFiles.set(fileName, i);
        }

        // Check if all files are done
        const allComplete = Array.from(this.processingFiles.values()).every(
            progress => progress === 100
        );
        if (allComplete) {
            this.uploadStatus = 'success';
        }
    };

    renderStepIndicator = (
        step: { number: number; label: string },
        index: number
    ) => {
        const { currentStep, steps } = this;

        return (
            <div key={step.number} className="d-flex align-items-center">
                <div className="d-flex align-items-center">
                    <div
                        className={`rounded-circle d-flex align-items-center justify-content-center fw-bold ${
                            currentStep >= step.number
                                ? 'bg-primary text-white'
                                : 'bg-light text-muted'
                        }`}
                        style={{
                            width: '36px',
                            height: '36px',
                            fontSize: '14px'
                        }}
                    >
                        {step.number}
                    </div>
                    <span
                        className={`ms-2 ${
                            currentStep >= step.number
                                ? 'text-dark fw-semibold'
                                : 'text-muted'
                        }`}
                        style={{ fontSize: '15px' }}
                    >
                        {step.label}
                    </span>
                </div>
                {index < steps.length - 1 && (
                    <div
                        className="mx-3"
                        style={{
                            width: '60px',
                            height: '2px',
                            backgroundColor:
                                currentStep > step.number
                                    ? '#0d6efd'
                                    : '#e0e0e0'
                        }}
                    />
                )}
            </div>
        );
    };

    render() {
        const { steps, currentStep } = this;

        return (
            <Card className="m-3" body>
                <div className="d-flex justify-content-center align-items-center mb-5">
                    {steps.map(this.renderStepIndicator)}
                </div>

                {currentStep === 1 && (
                    <Step1SelectFiles
                        selectedFiles={this.selectedFiles}
                        onFilesChange={this.handleFilesChange}
                        onNext={this.handleNextStep}
                    />
                )}
                {currentStep === 2 && (
                    <Step2Preview
                        selectedFiles={this.selectedFiles}
                        chunks={this.chunks}
                        totalChunks={this.totalChunks}
                        selectedFileIndex={this.selectedFileIndex}
                        onFileIndexChange={this.handleFileIndexChange}
                        onPrev={this.handlePrevStep}
                        onNext={this.handleNextStep}
                    />
                )}
                {currentStep === 3 && (
                    <Step3Processing
                        selectedFiles={this.selectedFiles}
                        uploadStatus={this.uploadStatus}
                        processingFiles={this.processingFiles}
                        onPrev={this.handlePrevStep}
                    />
                )}
            </Card>
        );
    }
}
